/**
 * Build the effective column list for ``DataViewTable``.
 *
 * Starts from the config's declared columns + any dynamic columns,
 * then auto-injects the bulk-select column when the row payload and
 * caller permissions warrant it:
 *
 *  - ``__select__`` (left-frozen) - bulk-select checkbox column. Sits
 *    at position 0 so the checkbox aligns with the row's leading
 *    edge. Only injected on the inspector variant and only when the
 *    caller can block.
 *
 * Per-row affordances (the Issues chip with note dot + drawer
 * trigger) live in the declared ``buildIssuesColumn()`` block. The
 * builder returns an unfrozen column; configs opt in to a freeze
 * edge if their layout needs it. The Issues chip is the only per-row
 * affordance now; the legacy ``__notes__`` and ``__actions__``
 * columns are gone.
 *
 * Finally on the platform variant we rewrite the ``fp_code`` link
 * template so clicking an FP code on a platform report jumps straight
 * to the per-FP exception view.
 *
 * Pure builder (called inside a ``useMemo``) so the host file owns
 * memoisation + the dependency array.
 */
import React from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'

import type {
  ExceptionColumnDef,
  ExceptionConfig,
  ExceptionContext,
} from '../../../../config/exceptions/types'
import { getExceptionObjectType } from '../../../../config/exceptions/registry'
import { buildRowInspectorLink } from '../../../../utils/urls'
import { poolwerxColors } from '../../../../theme/colors'

export interface BuildEffectiveColumnsArgs {
  config: ExceptionConfig
  data: Record<string, any>[]
  variant: ExceptionContext
  // Full API response (when available). Threaded to ``dynamicColumns`` so
  // builders can read response-level fields (e.g. the canonical ``outlets``
  // list the product tables build their per-outlet columns from).
  response?: Record<string, any>
  bulkColumnEligible: boolean
  isBulkEligible: (row: any) => boolean
  eligibleTagIdsOnPage: number[]
  selectedTagIds: Set<number>
  allEligibleSelected: boolean
  someEligibleSelected: boolean
  toggleAllOnPage: () => void
  toggleRow: (tagId: number) => void
}

export function buildEffectiveColumns({
  config,
  data,
  variant,
  response,
  bulkColumnEligible,
  isBulkEligible,
  eligibleTagIdsOnPage,
  selectedTagIds,
  allEligibleSelected,
  someEligibleSelected,
  toggleAllOnPage,
  toggleRow,
}: BuildEffectiveColumnsArgs): ExceptionColumnDef[] {
  const dynamic = config.dynamicColumns
    ? config.dynamicColumns(data, variant, response)
    : []
  const merged = [...config.columns, ...dynamic]

  // Bulk-select column - sits at position 0 (left-most), with a
  // tri-state header checkbox + per-row checkbox (hidden for
  // terminal-status rows). Auto-injected only when the viewer can
  // block and at least one row on the page is eligible.
  // Wrap each rendered Checkbox in a flex-centered Box that fills the
  // whole cell. Clicking anywhere in the 44px cell toggles the row -
  // the wrapper's onClick stops propagation so the row's drawer-open
  // handler can't fire. Paired with ``getCellStyle`` below which
  // zeroes the TableCell padding so the Box can truly fill its cell.
  const headerCheckboxSx = {
    color: variant === 'platform' ? '#fff' : poolwerxColors.teal,
    '&.Mui-checked': {
      color: variant === 'platform' ? '#fff' : poolwerxColors.teal,
    },
    '&.MuiCheckbox-indeterminate': {
      color: variant === 'platform' ? '#fff' : poolwerxColors.teal,
    },
  }
  const cellFillSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: 32,
    cursor: 'pointer',
  }
  const selectColumn: ExceptionColumnDef = {
    key: '__select__',
    label: '',
    align: 'center',
    width: 44,
    minWidth: 44,
    maxWidth: 44,
    freeze: 'left',
    group: 'identity',
    // Render once with rowSpan=2 on rowSplit tables (sales-compare
    // LS/Xero pairs) - the row is logically one sale, so the tick
    // controls one bulk-action target. Same approach the Issues
    // column uses; mirrors the Compare button's spanning behaviour.
    spansSystems: true,
    // Zero the body cell's padding + override the Checkbox component's
    // own padding so the flex-centered wrapper fills the cell edge to
    // edge. Without this, MUI's default TableCell padding leaves dead
    // zones around the checkbox where a click does nothing (or worse,
    // bubbles up to open the exception drawer).
    getCellStyle: () => ({
      padding: 0,
      textAlign: 'center',
      '& .MuiCheckbox-root': { padding: 0 },
    }),
    renderHeader: () => {
      const headerDisabled = eligibleTagIdsOnPage.length === 0
      return React.createElement(
        Box,
        {
          sx: {
            ...cellFillSx,
            cursor: headerDisabled ? 'default' : 'pointer',
          },
          onClick: (e: React.MouseEvent) => {
            if (headerDisabled) return
            e.stopPropagation()
            toggleAllOnPage()
          },
        },
        React.createElement(Checkbox, {
          size: 'small',
          checked: allEligibleSelected,
          indeterminate: someEligibleSelected,
          // ``onChange`` stays wired for keyboard / a11y; the cell's
          // ``onClick`` is the primary toggle path.
          onChange: toggleAllOnPage,
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          'aria-label': 'Select all rows on this page',
          disabled: headerDisabled,
          sx: { ...headerCheckboxSx, padding: 0 },
        }),
      )
    },
    render: (_v: any, row: Record<string, any>) => {
      if (!isBulkEligible(row)) return null
      const tagId = row.exception_tag_id as number
      return React.createElement(
        Box,
        {
          sx: cellFillSx,
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation()
            toggleRow(tagId)
          },
        },
        React.createElement(Checkbox, {
          size: 'small',
          checked: selectedTagIds.has(tagId),
          // ``onClick`` stopPropagation on the checkbox means the
          // wrapper's ``onClick`` doesn't fire for direct checkbox
          // clicks - so the toggle has to live in ``onChange`` for
          // that path, and on the wrapper for the cell-fill path.
          onChange: () => toggleRow(tagId),
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          'aria-label': `Select row ${row.object_id ?? tagId}`,
          sx: {
            color: poolwerxColors.teal,
            '&.Mui-checked': { color: poolwerxColors.teal },
            padding: 0,
          },
        }),
      )
    },
    exportFormat: 'text',
  }
  const withSelect = bulkColumnEligible ? [selectColumn, ...merged] : merged

  if (variant !== 'platform') return withSelect

  // Platform variant: open FP Code in a new tab and deep-link straight
  // to the per-FP exception view, tenure-pinned by the row's `fp_id`. All
  // codes in a config share one object_type (folder), so we resolve it once
  // via the first code and hand it to the shared builder as the override
  // (which still falls back to the dashboard link for rows without an
  // `exception_code`).
  const objectType =
    config.codes.length > 0 ? getExceptionObjectType(config.codes[0]) : null
  return withSelect.map((col) => {
    if (col.key !== 'fp_code') return col
    const builder = (row: Record<string, any>) =>
      buildRowInspectorLink(row, objectType)
    return {
      ...col,
      link: {
        ...col.link,
        builder,
        external: false,
        newTab: true,
      },
    }
  })
}
